import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import ContentLayout from '../../users/components/ContentLayout';
import TableSection from '../../users/components/TableSection';
import DeleteModal from '../../../components/modals/DeleteModal';
import CreateAdminModal from '../components/modals/CreateAdminModal';
import ResetAdminPinModal from '../components/modals/ResetAdminPinModal';
import { deleteAdmin, fetchAdmins } from '../api/adminsApi';
import { useAuth } from '../../../context/useAuth';
import useToast from '../../../shared/hooks/useToast';
import { formatDate } from '../../../shared/utils/formatDate';
import { formatUsername, isSuperAdmin } from '../../../shared/utils/roles';

const adminRoleLabels = {
  superadmin: 'Super admin',
  admin: 'Admin',
};

const getAdminName = (admin) =>
  [admin?.firstName, admin?.lastName].filter(Boolean).join(' ');

export default function AdminsPage() {
  const { user } = useAuth();
  const toast = useToast();

  const [admins, setAdmins] = useState([]);
  const [search, setSearch] = useState('');
  const [listLoading, setListLoading] = useState(false);

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [pinAdmin, setPinAdmin] = useState(null);
  const [adminToDelete, setAdminToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteDone, setDeleteDone] = useState(false);

  const canManage = isSuperAdmin(user);

  const loadAdmins = useCallback(async () => {
    setListLoading(true);
    const { data, ok, message } = await fetchAdmins();
    setListLoading(false);

    if (ok) {
      setAdmins(data);
    } else if (message) {
      toast.error(message);
    }
  }, [toast]);

  useEffect(() => {
    if (canManage) loadAdmins();
  }, [canManage, loadAdmins]);

  // Admina je malo, pa se pretrazuje lokalno (ime, prezime, korisnicko ime, email).
  const filteredAdmins = useMemo(() => {
    const query = search.trim().toLowerCase().replace(/^@+/, '');
    if (!query) return admins;
    return admins.filter((admin) =>
      [getAdminName(admin), admin.username, admin.email]
        .filter(Boolean)
        .some((value) => value.toLowerCase().includes(query))
    );
  }, [admins, search]);

  const handleDeleteClose = () => {
    setAdminToDelete(null);
    setDeleteLoading(false);
    setDeleteDone(false);
  };

  const handleDeleteAdmin = async () => {
    if (!adminToDelete?._id) return;

    setDeleteLoading(true);
    const response = await deleteAdmin(adminToDelete._id);

    if (!response.ok) {
      toast.error(response.message);
      setDeleteLoading(false);
      return;
    }

    setDeleteDone(true);
    toast.success(response.message);
    await loadAdmins();

    setTimeout(() => {
      handleDeleteClose();
    }, 700);
  };

  const isSelf = (admin) => admin?._id === user?._id;

  // U sistemu uvek mora ostati bar jedan admin (bek to i proverava), pa se
  // poslednjem adminu ne nudi brisanje.
  const adminCount = admins.filter((admin) => admin.role === 'admin').length;

  const columns = useMemo(
    () => [
      {
        key: 'firstName',
        label: 'Ime i prezime',
        render: (_, row) => getAdminName(row) || '-',
      },
      {
        key: 'username',
        label: 'Korisničko ime',
        render: (val) => formatUsername(val) || '-',
      },
      {
        key: 'email',
        label: 'Email',
        render: (val) => val || '-',
      },
      {
        key: 'role',
        label: 'Uloga',
        render: (val) => adminRoleLabels[val] || val || '-',
      },
      {
        key: 'createdAt',
        label: 'Dodat',
        render: (val) => (val ? formatDate(val) : '-'),
      },
    ],
    []
  );

  if (!canManage) {
    return <Navigate to="/neovlascen" replace />;
  }

  return (
    <>
      <ContentLayout>
        <TableSection
          showCreateButton
          createButtonDisplay="Dodaj admina"
          onCreateClick={() => setIsCreateModalOpen(true)}
          onSearch={setSearch}
          columns={columns}
          actions={[
            {
              key: 'resetPin',
              label: 'Resetuj PIN',
              icon: (
                <img
                  src="/assets/icons/edit-icon.svg"
                  alt="Resetuj PIN"
                  width={16}
                  height={16}
                  className="dropdown-icon"
                />
              ),
              onClick: (row) => setPinAdmin(row),
              // PIN drugog super admina ne moze da se menja (samo svoj).
              visible: (row) => row?.role === 'admin' || isSelf(row),
            },
            {
              key: 'delete',
              label: 'Obriši admina',
              icon: (
                <img
                  src="/assets/icons/delete.svg"
                  alt="Obriši"
                  width={16}
                  height={16}
                  className="dropdown-icon"
                />
              ),
              onClick: (row) => setAdminToDelete(row),
              visible: (row) =>
                row?.role === 'admin' && !isSelf(row) && adminCount > 1,
              danger: true,
            },
          ]}
          data={filteredAdmins}
          emptyState={{
            icon: '/assets/icons/no-users.svg',
            title: listLoading ? 'Učitavanje...' : 'Nema admina',
            description: listLoading ? '' : 'Dodajte admina.',
          }}
          searchPlaceholder="Pretraži admine"
        />
      </ContentLayout>
      <CreateAdminModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSaved={loadAdmins}
      />
      <ResetAdminPinModal
        isOpen={Boolean(pinAdmin)}
        onClose={() => setPinAdmin(null)}
        admin={pinAdmin}
      />
      <DeleteModal
        visible={Boolean(adminToDelete)}
        onDelete={handleDeleteAdmin}
        onCancel={handleDeleteClose}
        loading={deleteLoading}
        done={deleteDone}
        title="Brisanje admina"
        message={`Da li ste sigurni da želite da obrišete admina ${getAdminName(
          adminToDelete
        )} (${formatUsername(
          adminToDelete?.username
        )})? Nalog više neće moći da se prijavi.`}
        buttonText="Obriši admina"
      />
    </>
  );
}
